import { useStore } from '@store/index';

import { ANALYTICS_SDK_DISABLED_ORG_IDS } from '@/constants/feature-flags';
import {
  AnalyticsEventType,
  type AppEventPayload,
  type AppEventsBatch,
  type TrackContext,
} from '@/types/analytics';

import { detectBrowser, detectOS, getAppVersion, getSessionId } from './context';
import * as store from './store';
import { sendBatch, sendBatchBeacon } from './transport';

const MAX_BATCH = 10;
const FLUSH_MS = 60000;
const CLICK_ATTR = 'data-analytics-id';
const APP_NAME = 'compass-dashboard';
const LOCK_NAME = 'compass-analytics-flush';

let initialized = false;
let flushing = false;
let intervalId: ReturnType<typeof setInterval> | null = null;

function currentOrgId(): string | null {
  return useStore.getState().selectedOrganizationId ?? null;
}

/**
 * HIPAA kill switch: the frontend analytics SDK is fully disabled — no events are ever
 * enqueued or sent — when no organization is selected, or when the selected organization is
 * in `ANALYTICS_SDK_DISABLED_ORG_IDS`.
 */
export function isAnalyticsEnabled(): boolean {
  const orgId = currentOrgId();
  if (!orgId) return false;
  return !ANALYTICS_SDK_DISABLED_ORG_IDS.has(orgId);
}

function generateEventId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random()}`;
}

function currentUrlPath(): string | undefined {
  return typeof window !== 'undefined' ? window.location.pathname : undefined;
}

function buildBatch(events: AppEventPayload[]): AppEventsBatch {
  // org_id lives per-event (validated server-side; non-member events are dropped), so a single
  // flush — even one spanning an org switch — sends as ONE batch. No per-org grouping.
  return {
    app: { name: APP_NAME, version: getAppVersion() },
    device: { browser: detectBrowser(), os: detectOS() },
    session: { id: getSessionId() },
    events,
  };
}

// Acquire a cross-tab exclusive Web Lock before flushing, so two tabs never race to send/clear
// the same durably-stored events. Falls back to running `fn` directly when the Web Locks API is
// unavailable (older browsers) — losing cross-tab exclusivity but never blocking the flush.
async function withLock(fn: () => Promise<void>): Promise<void> {
  // `navigator.locks` is typed as always-present in lib.dom.d.ts, but is absent at runtime in
  // older browsers and in jsdom — guard defensively rather than trust the type.
  const locks = navigator.locks as LockManager | undefined;
  if (locks?.request) {
    await locks.request(LOCK_NAME, { mode: 'exclusive' }, fn);
  } else {
    await fn();
  }
}

/**
 * Drain durably-stored events and send them as one batch. Guarded against in-tab reentrancy
 * (`flushing`) and cross-tab races (Web Lock). On success, sent events are removed from
 * storage; on failure they are left in place for the next attempt (at-least-once delivery).
 */
export async function flush(): Promise<void> {
  if (flushing) return;
  flushing = true;
  try {
    await withLock(async () => {
      const pending = store.snapshot();
      if (pending.length === 0) return;
      const ok = await sendBatch(buildBatch(pending));
      if (ok) store.removeByIds(pending.map(e => e.event_id));
      // on failure: leave events in storage for the next attempt (at-least-once)
    });
  } catch {
    // `navigator.locks.request(...)` can throw/reject in some edge browser states (e.g. the
    // page is being torn down, or a security/permissions-policy restriction on the Locks API).
    // Every caller does `void flush()`, so an escaping rejection here would become an unhandled
    // promise rejection in the app. Swallow it and simply leave events in storage — they'll be
    // retried on the next interval, same as a failed send (analytics is loss-tolerant).
  } finally {
    flushing = false;
  }
}

// Best-effort flush for pagehide/visibilitychange-hidden: we cannot await a response before the
// page unloads, so this fires the send without clearing storage. A rare duplicate delivery on
// unload is acceptable — analytics is loss-tolerant and ClickHouse has no dedup — while a
// dropped-on-unload event would not be.
function flushBeacon(): void {
  const pending = store.snapshot();
  if (pending.length === 0) return;
  sendBatchBeacon(buildBatch(pending));
}

function emit(type: number, name: string, ctx?: TrackContext): void {
  if (!isAnalyticsEnabled()) return;
  // Captured at enqueue time: isAnalyticsEnabled() above guarantees an org is selected, so
  // this is always a real org id — never null — for the lifetime of this queued event.
  const orgId = currentOrgId() as string;
  const zustandStore = useStore.getState();
  const payload: AppEventPayload = {
    event_id: generateEventId(),
    event_time: Date.now(),
    event_type: type,
    event_name: name,
    org_id: orgId,
    program_id: ctx?.programId ?? zustandStore.selectedProgramId ?? undefined,
    route_pattern: ctx?.routePattern,
    case_record_id: ctx?.caseRecordId,
    target_id: ctx?.targetId,
    // PATHNAME ONLY — never `search`/`hash`, which can carry query params/PHI-adjacent data.
    url_path: currentUrlPath(),
    props: ctx?.props,
  };
  store.append(payload);
  if (store.loadPending().length >= MAX_BATCH) {
    void flush();
  }
}

export function trackEvent(type: number, name: string, ctx?: TrackContext): void {
  emit(type, name, ctx);
}

// Stable event name for every navigation — the specific page is carried in `route_pattern`, not
// encoded into `event_name`. Follows the `domain:entity[:sub]:action` grammar (see README).
const PAGE_VIEW_EVENT_NAME = 'page:view';

export function trackPageView(routePattern: string, ctx?: TrackContext): void {
  emit(AnalyticsEventType.PAGE_VIEW, PAGE_VIEW_EVENT_NAME, { ...ctx, routePattern });
}

/**
 * Public convenience for recording a CLICK event from application code (as opposed to the
 * automatic `data-analytics-id` capture below). Mirrors what the internal listener does —
 * `targetId` defaults to `name` unless overridden via `ctx`.
 */
export function trackClick(name: string, ctx?: TrackContext): void {
  trackEvent(AnalyticsEventType.CLICK, name, { targetId: name, ...ctx });
}

/**
 * Public convenience for recording an INPUT event (text/field fill or typing) from application
 * code. Mirrors `trackClick` — `targetId` defaults to `name` unless overridden via `ctx`.
 */
export function trackInput(name: string, ctx?: TrackContext): void {
  trackEvent(AnalyticsEventType.INPUT, name, { targetId: name, ...ctx });
}

/**
 * Public convenience for recording a CHANGE event (select/checkbox/radio/date picker) from
 * application code. Mirrors `trackClick` — `targetId` defaults to `name` unless overridden via
 * `ctx`.
 */
export function trackChange(name: string, ctx?: TrackContext): void {
  trackEvent(AnalyticsEventType.CHANGE, name, { targetId: name, ...ctx });
}

/**
 * Pure, testable composer for wiring a tracked click onto an existing onClick handler (used by
 * `<TrackClick>`). The child's own handler always runs FIRST — tracking is fire-and-forget and
 * must never block or alter the child's behavior. `trackClick` is wrapped in try/catch so an
 * analytics failure can never throw into the caller's click handler.
 */
export function composeTrackedClick<E extends { preventDefault?: () => void }>(
  childOnClick: ((e: E) => void) | undefined,
  name: string,
  ctx?: TrackContext,
): (e: E) => void {
  return (e: E) => {
    childOnClick?.(e);
    try {
      trackClick(name, ctx);
    } catch {
      // Analytics must never break the child's click behavior — swallow and move on.
    }
  };
}

// Automatic DOM click capture records only the static `data-analytics-id` value — never DOM
// text and never other attributes. Additional non-PHI context must be passed via explicit
// trackEvent(type, name, { props }) calls in code.
//
// This is a structural guarantee, not a convention: there is no dataset sweep here at all
// (fires ONLY for elements carrying data-analytics-id, or a descendant of one — untagged
// clicks are ignored, allowlist), so no other data-analytics-* attribute an author adds to a
// tagged element can ever leak into an event, and no innerText/textContent/input value is ever
// read.
function handleClick(ev: MouseEvent): void {
  const start = ev.target;
  // Defensive: a capture-phase document listener should never throw. `ev.target` is typed as
  // EventTarget and is not guaranteed to be an Element (e.g. it can be a Document or Window in
  // some synthetic/edge dispatches) — bail out rather than let `.closest()` throw.
  if (!(start instanceof Element)) return;
  const el = start.closest(`[${CLICK_ATTR}]`) as HTMLElement | null;
  if (!el) return; // untagged click — ignored (allowlist)
  const name = el.getAttribute(CLICK_ATTR);
  if (!name) return;
  trackClick(name);
}

// Attached once at app bootstrap, BEFORE any organization is selected — so this must never be
// gated on isAnalyticsEnabled() (which requires a selected org). Gating init here would
// permanently disable tracking for the whole session. Instead, listeners/interval are attached
// unconditionally exactly once, and enablement is evaluated per-event in emit() — the org is
// known by the time an event actually fires, even though it isn't known at bootstrap.
export function initAnalytics(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;
  if (!intervalId) {
    intervalId = setInterval(() => void flush(), FLUSH_MS);
  }
  document.addEventListener('click', handleClick, true); // capture phase
  window.addEventListener('pagehide', flushBeacon);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushBeacon();
  });
  // Ship anything persisted from a prior session or a failed send before this page unloads too.
  void flush();
}

// Test-only: reset module state. Harmless in production — it is never imported/called outside
// tests, and simply clearing the flags/interval is a no-op side effect if it ever were.
export function __resetAnalyticsForTest(): void {
  if (intervalId) {
    clearInterval(intervalId);
    intervalId = null;
  }
  initialized = false;
  flushing = false;
}

// Re-exported so `import { useTrackEvent } from '@/services/analytics'` works alongside the
// functions above.
/** @public Public API surface of the analytics module (see ./useTrackEvent). */
export { useTrackEvent } from './useTrackEvent';
